import React, {useEffect, useReducer, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import * as api from "../../../services/api";

import {
    ResponsiveStepper,
    StepperContext,
    useStepperContext
} from "../../../components/ResponsiveStepper";
import {useFeedbackTools} from "../../../components/FeedbackTools";
import {apartmentRequestMapper} from "../../../mapper/quoteMapper";
import {isValidDateObject} from "../../../services/date/validators";
import {useQuotes} from "../QuotesContext";
import StepGuest, {getNationalityLanguage, useStepGuest} from "./StepGuest";
import StepVariants, {useStepVariants} from "./StepVariants";
import StepDetails, {useStepDetails} from "./StepDetails";
import StepBookingConditions, {useStepBookingConditions} from "./StepBookingConditions";
import useVariantGenerator from "./useVariantGenerator";
import Alert from "@mui/material/Alert";


const FormQuote = () => {
    // It only provides the StepperContext used by the ResponsiveStepper
    return <StepperContext>
        <FormQuoteStepper/>
    </StepperContext>
}

const FormQuoteStepper = () => {
    const {createQuote, formQuote} = useQuotes();
    const {next, activeStep} = useStepperContext();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const {showErrorNotification} = useFeedbackTools();
    const [data, updateData] = useReducer((state, partialState) => ({...state, ...partialState}), {})

    // used to check whether there exist another quote for the same apartment request
    const [duplicateQuote, setDuplicateQuote] = useState(false);

    // Get the Apartment request data from url params
    const apartmentRequest = apartmentRequestMapper({
        name: searchParams.get("name") === null ? null : decodeURIComponent(searchParams.get("name")),
        surname: searchParams.get("surname") === null ? null : decodeURIComponent(searchParams.get("surname")),
        email: searchParams.get("email") === null ? null : decodeURIComponent(searchParams.get("email")),
        nationality: searchParams.get("nationality"),
        language: searchParams.get("language"),
        arrival: searchParams.get("arrival"),
        departure: searchParams.get("departure"),
        adults: searchParams.get("adults"),
        children: searchParams.get("children"),
        petsNumber: searchParams.get("pets_number"),
        homeId: searchParams.get("home_id"),
        id: searchParams.get("request_id")
    });

    const handlePartialSubmit = (values) => {
        updateData(values);
        next();
    }

    const handleSubmit = async (values) => {
        let requestData = {...data, ...values, requestId: isNaN(apartmentRequest.id) ? null : apartmentRequest.id };
        try{
            formQuote.setIsLoading(true);
            const quote = await createQuote(requestData);
            formQuote.close();
            navigate(`/main/quotes/${quote.id}/preview`);
        }catch (e){
            showErrorNotification(e);
            formQuote.setIsLoading(false);
        }
    }

    const {formik: stepGuestFormik} = useStepGuest({
        name: apartmentRequest.name,
        surname: apartmentRequest.surname,
        email: apartmentRequest.email,
        nationality: apartmentRequest.nationality?.code,
        language: apartmentRequest.language || (apartmentRequest.nationality?.code ? getNationalityLanguage(apartmentRequest.nationality.code) : ""),
        onSubmit: (values) => handlePartialSubmit(values)}
    );

    const {formik: stepDetailsFormik} = useStepDetails({
        arrival: apartmentRequest.arrival,
        departure: apartmentRequest.departure,
        adults: apartmentRequest.adults,
        children: apartmentRequest.children,
        petsNumber: apartmentRequest.petsNumber,
        onSubmit: (values) => handlePartialSubmit(values)
    })

    const {formik: stepVariantsFormik} = useStepVariants({
        onSubmit: (values) => handlePartialSubmit(values)
    })

    const {formik: stepBookingConditionsFormik} = useStepBookingConditions({
        arrival: stepDetailsFormik.values.arrival,
        onSubmit: handleSubmit
    })

    // Skip step 0 and step 1 if the initial values are already valid
    useEffect(
        () => {
            const updateActiveStep = async () => {
                const [step0Errors, step1Errors] = await Promise.all([
                    stepGuestFormik.validateForm(),
                    stepDetailsFormik.validateForm()
                ]);
                if(Object.entries(step0Errors).length === 0){
                    await stepGuestFormik.submitForm();
                    if(Object.entries(step1Errors).length === 0){
                        await stepDetailsFormik.submitForm();
                    }
                }
            }
            updateActiveStep();
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    );

    const stepFormik = [stepGuestFormik, stepDetailsFormik, stepVariantsFormik, stepBookingConditionsFormik];
    const disableNext = (
        Object.keys(stepFormik[activeStep].errors).some(x => stepFormik[activeStep].touched[x])
    );

    const name = stepGuestFormik.values.name;
    const surname = stepGuestFormik.values.surname;
    const adults = stepDetailsFormik.values.adults;
    const children = stepDetailsFormik.values.children;
    const petsNumber = stepDetailsFormik.values.petsNumber;
    const arrival = stepDetailsFormik.values.arrival;
    const departure = stepDetailsFormik.values.departure;
    const homeId = apartmentRequest.homeId;

    const handleNext = (e) => {
        stepFormik[activeStep].handleSubmit(e);
    }

    const variantsGenerator = useVariantGenerator({arrival, departure, adults, children, petsNumber, homeId});

    // Update the date of the variants when arrival or departure change
    useEffect(
        () => {
            if(stepVariantsFormik.values.quoteVariants.length > 0
                && isValidDateObject(arrival)
                && isValidDateObject(departure)){
                stepVariantsFormik.setFieldValue(
                    'quoteVariants',
                    variantsGenerator.updateVariantsDate(stepVariantsFormik.values.quoteVariants));
            }
        },
        [arrival, departure]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    // Check if there exist others quotes for the same request id
    useEffect(
        () => {
            const innerLoad = async () => {
                const quotes = await api.Quotes.getAllQuotes({request_id: apartmentRequest.id});
                if(quotes.itemsCount > 0){
                    setDuplicateQuote(true);
                }
            }
            if(!isNaN(apartmentRequest.id)){
                innerLoad();
            }
        },
        [apartmentRequest.id]
    )

    return <ResponsiveStepper title={`Preventivo ${surname} ${name}`}
                              isOpen={true}
                              isSaving={formQuote.loading}
                              onClose={formQuote.close}
                              onNext={handleNext}
                              onFinish={stepBookingConditionsFormik.handleSubmit}
                              stepLabels={["Anagrafica", "Dettagli", "Alternative", "Condizioni"]}
                              disableNext={disableNext}
                              disableBackdropClose
                              saveLabel="Prosegui"
                              commonHeader={ <>{duplicateQuote && <Alert severity="warning" sx={{mb: 2}}>
                                  Attenzione, hai già creato un preventivo per la richiesta di soggiorno selezionata!
                              </Alert>}</>}
    >
        <StepGuest formik={stepGuestFormik}/>
        <StepDetails formik={stepDetailsFormik}/>
        <StepVariants formik={stepVariantsFormik} variantsGenerator={variantsGenerator} petsNumber={petsNumber}/>
        <StepBookingConditions formik={stepBookingConditionsFormik} arrival={arrival}/>
    </ResponsiveStepper>
}

export default FormQuote